// src/context/admin/PermissionContext.tsx

import BackendService from 'BackendService';
import { Permissions, UserPermissions } from 'Permission';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface PermissionContextType {
  hasPermission: (p: Permissions) => boolean;
  loading: boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<UserPermissions>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Replace with your real permission loading logic
    new BackendService()
      .getSelfPermissions()
      .then((permissions) => {
        setPermissions(permissions);
        setLoading(false);
      })
      // A non-admin session 403s here. Without this the provider is stuck on
      // `loading` forever and every admin route it wraps renders blank — fall
      // through to an empty permission set instead.
      .catch(() => setLoading(false));
  }, []);

  const hasPermission = (perm: Permissions) => {
    return permissions[perm] === true;
  };

  if (loading) return null; // Or render a loading spinner here

  return (
    <PermissionContext.Provider value={{ hasPermission, loading: false }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) throw new Error('usePermissions must be used within PermissionProvider');
  return context;
};
// At the end of src/context/admin/PermissionContext.tsx

interface PermissionGateProps {
  permissionKey: Permissions;
  children: ReactNode;
  fallback?: ReactNode; // Optional fallback UI if permission is not met
}

interface PermissionGateOptionalProps {
  permissionKey?: Permissions;
  children: ReactNode;
  fallback?: ReactNode; // Optional fallback UI if permission is not met
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  permissionKey,
  children,
  fallback = null,
}) => {
  const { hasPermission } = usePermissions();

  // Optional: Handling of loading state.
  // If you want to avoid rendering children or fallback until permissions are loaded:
  // if (loading) {
  //   return null; // Or a specific loader, though often parent components handle overall loading UI
  // }

  if (hasPermission(permissionKey)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

export const PermissionGateOptional: React.FC<PermissionGateOptionalProps> = ({
  permissionKey,
  children,
  fallback = null,
}) => {
  if (permissionKey) {
    return (
      <PermissionGate fallback={fallback} permissionKey={permissionKey}>
        {children}
      </PermissionGate>
    );
  }

  return <>{children}</>;
};
